<template>
  <div class="relative">
    <div
      ref="sliderContainer"
      class="flex flex-nowrap h-full overflow-x-auto lg:overflow-x-hidden snap-x snap-mandatory"
    >
      <div
        v-for="image in images"
        :key="image.id"
        ref="sliderItems"
        class="min-w-[40%] lg:min-w-[35%] w-auto snap-start mr-2 lg:mr-8 last:mr-0 h-full rounded-md relative shrink-0"
        :class="{ 'min-w-[calc(70%+2rem)]': images.length === 2 }"
      >
        <strapi-image
          class="rounded-md h-full shadow-md"
          :image="image"
          :alt="image.alternativeText"
          as-background
        />
      </div>
    </div>

    <button
      v-if="activeItemIndex > 0"
      aria-label="Previous"
      class="absolute hidden lg:block top-1/2 -translate-y-1/2 rounded-md px-3 py-2 bg-depth-3 text-mouse hover:bg-depth-4 transition-colors"
      type="button"
      @click="previousItem"
    >
      <fa-icon
        class="text-rhino size-4"
        :icon="faChevronLeft"
        aria-hidden="true"
      />
    </button>

    <button
      v-if="activeItemIndex < images.length - 1 && !arrivedState.right"
      aria-label="Next"
      class="absolute hidden lg:block top-1/2 -translate-y-1/2 left-[calc(70%+0.5rem)] rounded-md px-3 py-2 bg-depth-3 text-mouse hover:bg-depth-4 transition-colors"
      type="button"
      @click="nextItem"
    >
      <fa-icon
        class="text-rhino size-4"
        :icon="faChevronRight"
        aria-hidden="true"
      />
    </button>
  </div>
</template>

<script setup lang="ts">
import {
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import type { StrapiImage } from '~/apollo/types/types';

const props = withDefaults(
  defineProps<{
    images?: StrapiImage[];
  }>(),
  {
    images: () => [],
  },
);

const activeItemIndex = ref<number>(0);
const sliderContainer = ref<HTMLDivElement>(null);
const sliderItems = ref<Array<HTMLDivElement>>([]);
const { arrivedState } = useScroll(sliderContainer);

const scroll = () => {
  let targetScrollPosition = 0;

  if (activeItemIndex.value > 0) {
    targetScrollPosition = sliderItems.value[activeItemIndex.value].offsetLeft;
  }

  sliderContainer.value.scrollTo({
    left: targetScrollPosition,
    behavior: 'smooth',
  });
};

const previousItem = () => {
  activeItemIndex.value -= 1;
  scroll();
};

const nextItem = () => {
  activeItemIndex.value += 1;
  scroll();
};

useWindowResize(
  () => {
    activeItemIndex.value = 0;
    scroll();
  },
  { throttle: 100 },
);
</script>
